import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getFieldNoteBySlug, formatDate } from '@/data/fieldNotes';

const FieldNotePost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const note = slug ? getFieldNoteBySlug(slug) : undefined;

  if (!note) {
    return <Navigate to="/field-notes" replace />;
  }

  // Convert content newlines to paragraphs
  const paragraphs = note.content.split('\n\n').filter(p => p.trim());

  return (
    <div className="min-h-screen bg-maslow-cream">
      <Helmet>
        <title>{note.title} — Maslow</title>
        <link rel="canonical" href={`https://maslow.nyc/field-notes/${note.slug}`} />
        <meta name="description" content={note.excerpt} />
        <meta property="og:title" content={`${note.title} — Maslow`} />
        <meta property="og:description" content={note.excerpt} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="max-w-[680px] mx-auto px-6 pt-14 pb-24">
        {/* Back Link */}
        <Link
          to="/field-notes"
          className="inline-block text-sm text-[color-mix(in_srgb,var(--navy)_55%,transparent)] hover:text-maslow-gold transition-colors mb-10"
        >
          &larr; Field Notes
        </Link>

        {/* Article Header */}
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.16em] text-[color-mix(in_srgb,var(--navy)_50%,transparent)] mb-3">
            {formatDate(note.date)}
            {note.tags.length > 0 && (
              <span className="text-[color-mix(in_srgb,var(--navy)_40%,transparent)]">
                {'  ·  '}
                {note.tags.join(' · ')}
              </span>
            )}
          </p>
          <h1 className="text-3xl md:text-[40px] font-serif text-maslow-navy leading-tight">
            {note.title}
          </h1>
          <div className="w-14 h-px bg-maslow-gold mt-6" />
        </header>

        {/* Article Body */}
        <article>
          {paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className="text-lg text-[color-mix(in_srgb,var(--navy)_88%,transparent)] mb-6"
              style={{ lineHeight: '1.8' }}
            >
              {paragraph}
            </p>
          ))}
        </article>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-[color-mix(in_srgb,var(--navy)_10%,transparent)]">
          <Link
            to="/field-notes"
            className="inline-block text-sm text-maslow-gold hover:text-maslow-navy transition-colors"
          >
            &larr; Back to Field Notes
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default FieldNotePost;
